import React, { useMemo, useState } from 'react'
import { buildPreviewSrcDoc } from '../previewHtml'
import { compileEmail, wrapPreviewDocument } from './blocks/compileEmail'
import { renderPreview, sampleById } from './editor/samples'
import { DEFAULT_CTA_TEXT, docCouponCode } from './useCampaignDraft'
import type { CompileContext, EmailDoc } from './blocks/types'
import {
  FONT,
  PREVIEW_INNER_W,
  R,
  SIDEBAR_W,
  T,
  sectionLabel,
} from './builderStyles'

// Sticky right sidebar (prototype L2637–2731), hidden on the Content step:
// a mock mail window over a scaled render of the compiled email, with a
// working Desktop (600) / Mobile (375) toggle, then Estimated Reach from the
// real audience-preview counts.

const DEVICE_W = { desktop: 600, mobile: 375 } as const
const FRAME_H = 1000

export interface ReachInfo {
  // 'flow' = auto-targeted automation.
  kind: 'flow' | 'audience'
  label: string
  dots: string[]
  total: number | null
  emailEligible: number | null
  smsEligible: number | null
  loading: boolean
}

export interface LivePreviewSidebarProps {
  isSms: boolean
  smsBody: string
  fromName: string
  fromEmail: string
  subject: string
  preview: string
  doc: EmailDoc
  ctx: CompileContext
  // Store segments, so conditional blocks resolve against the sample customer.
  segments?: Array<{ id: string }>
  // false (new campaign, nothing chosen yet) → "Select a campaign type…".
  hasType?: boolean
  reach: ReachInfo
}

const NO_SEGMENTS: Array<{ id: string }> = []

const fmt = (n: number | null) => (n === null ? '—' : n.toLocaleString())

const MailField: React.FC<{
  label: string
  value: string
  muted?: boolean
}> = ({ label, value, muted }) => (
  <div
    style={{
      background: T.white,
      padding: '8px 12px',
      borderBottom: `1px solid ${T.gray100}`,
      fontSize: 11,
      display: 'flex',
      gap: 6,
    }}
  >
    <span style={{ color: T.gray400, flexShrink: 0 }}>{label}</span>
    <span
      style={{
        color: muted ? T.gray400 : T.gray700,
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </div>
)

// Prototype empty-state email (L2684–2694): banner, logo pill, hero
// placeholder, subject, dashed offer box, CTA bar, footer — in the store's
// brand colour.
const SkeletonEmail: React.FC<{ brand: string; subject: string }> = ({
  brand,
  subject,
}) => {
  const tint = `color-mix(in srgb, ${brand} 12%, white)`
  return (
    <div style={{ background: T.white, fontFamily: FONT, textAlign: 'left' }}>
      <div
        style={{
          height: 8,
          background: `linear-gradient(90deg, ${brand}, color-mix(in srgb, ${brand} 55%, white))`,
        }}
      />
      <div style={{ padding: '20px 24px' }}>
        <div
          style={{
            width: 80,
            height: 20,
            background: tint,
            borderRadius: 3,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 700,
            color: brand,
          }}
        >
          BREADSTACK
        </div>
        <div
          style={{
            height: 80,
            background: `linear-gradient(135deg, ${tint}, ${T.blueBg})`,
            borderRadius: R.sm,
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            color: T.gray400,
          }}
        >
          [ Hero Image ]
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: T.gray800,
            lineHeight: 1.4,
            marginBottom: 8,
          }}
        >
          {subject.trim() || 'Your subject line'}
        </div>
        <div
          style={{
            fontSize: 12,
            color: T.gray600,
            lineHeight: 1.6,
            marginBottom: 14,
          }}
        >
          Your message will appear here…
        </div>
        <div
          style={{
            background: tint,
            border: `1.5px dashed ${brand}`,
            borderRadius: R.sm,
            padding: '10px 14px',
            textAlign: 'center',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: brand,
              letterSpacing: '0.08em',
              fontFamily: 'monospace',
            }}
          >
            CODE
          </div>
          <div style={{ fontSize: 11, color: T.gray600, marginTop: 3 }}>
            Your offer
          </div>
        </div>
        <div
          style={{
            background: brand,
            color: T.white,
            padding: '10px 20px',
            borderRadius: R.sm,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 700,
            marginBottom: 14,
          }}
        >
          {DEFAULT_CTA_TEXT}
        </div>
        <div
          style={{
            fontSize: 10,
            color: T.gray400,
            textAlign: 'center',
            lineHeight: 1.7,
            borderTop: `1px solid ${T.gray100}`,
            paddingTop: 12,
          }}
        >
          You're receiving this because you opted in.
          <br />
          <span style={{ color: T.blue }}>Unsubscribe</span>
        </div>
      </div>
    </div>
  )
}

const EmailMiniPreview = React.memo<{
  doc: EmailDoc
  ctx: CompileContext
  segments: Array<{ id: string }>
  preview: string
  subject: string
  device: keyof typeof DEVICE_W
}>(({ doc, ctx, segments, preview, subject, device }) => {
  // Evaluate the compiled markers the way the backend does at send time
  // (tokens, conditional / wallet variants, cart rows) so no raw {{tokens}}
  // reach the mini preview.
  const srcDoc = useMemo(
    () =>
      !doc.blocks.length
        ? ''
        : buildPreviewSrcDoc(
            wrapPreviewDocument(
              renderPreview(
                compileEmail(doc, { ...ctx, target: 'email' }),
                sampleById('gold'),
                segments,
                ctx,
                docCouponCode(doc),
              ),
              doc.settings,
              preview,
            ),
          ),
    [doc, ctx, segments, preview],
  )
  const width = DEVICE_W[device]
  const scale = PREVIEW_INNER_W / width
  if (!doc.blocks.length)
    return <SkeletonEmail brand={doc.settings.brand} subject={subject} />
  return (
    <div
      style={{
        width: PREVIEW_INNER_W,
        height: Math.round(FRAME_H * scale),
        overflow: 'hidden',
        margin: '0 auto',
      }}
    >
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={srcDoc}
        style={{
          width,
          height: FRAME_H,
          border: 0,
          display: 'block',
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
          background: T.white,
        }}
      />
    </div>
  )
})
EmailMiniPreview.displayName = 'EmailMiniPreview'

const DeviceBtn: React.FC<{
  active: boolean
  onClick: () => void
  children: React.ReactNode
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    aria-pressed={active}
    className="bsb-dev"
    onClick={onClick}
    style={{
      flex: 1,
      padding: 6,
      border: `1.5px solid ${active ? T.blue : T.gray200}`,
      borderRadius: R.sm,
      fontSize: 12,
      fontWeight: 500,
      color: active ? T.blue : T.gray500,
      background: active ? T.blueBg : T.white,
      cursor: 'pointer',
      fontFamily: FONT,
    }}
  >
    {children}
  </button>
)

export const LivePreviewSidebar: React.FC<LivePreviewSidebarProps> = ({
  isSms,
  smsBody,
  fromName,
  fromEmail,
  subject,
  preview,
  doc,
  ctx,
  segments = NO_SEGMENTS,
  hasType = true,
  reach,
}) => {
  const [device, setDevice] = useState<keyof typeof DEVICE_W>('desktop')
  return (
    <aside
      style={{
        width: SIDEBAR_W,
        flexShrink: 0,
        boxSizing: 'border-box',
        borderLeft: `1px solid ${T.gray200}`,
        background: T.white,
        padding: 24,
        position: 'sticky',
        top: 105,
        alignSelf: 'flex-start',
        maxHeight: 'calc(100vh - 105px)',
        overflowY: 'auto',
        fontFamily: FONT,
      }}
    >
      <div style={sectionLabel}>Live Preview</div>
      {!hasType ? (
        <div
          style={{
            border: `1px solid ${T.gray200}`,
            borderRadius: R.md,
            padding: '28px 22px',
            textAlign: 'center',
            fontSize: 12,
            color: T.gray400,
          }}
        >
          Select a campaign type to see a preview.
        </div>
      ) : isSms ? (
        <div
          style={{
            border: `1px solid ${T.gray200}`,
            borderRadius: R.md,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: T.gray800,
              padding: '12px 16px',
              textAlign: 'center',
              color: T.white,
              fontSize: 10,
            }}
          >
            SMS PREVIEW
          </div>
          <div style={{ padding: 16, background: '#F0F0F0', minHeight: 120 }}>
            <div
              style={{
                background: T.white,
                borderRadius: '14px 14px 14px 2px',
                padding: '10px 14px',
                fontSize: 12.5,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                color: T.gray800,
              }}
            >
              {smsBody.trim() || 'Your message will appear here…'}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <DeviceBtn
              active={device === 'desktop'}
              onClick={() => setDevice('desktop')}
            >
              📧 Email
            </DeviceBtn>
            <DeviceBtn
              active={device === 'mobile'}
              onClick={() => setDevice('mobile')}
            >
              📱 Mobile
            </DeviceBtn>
          </div>
          <div
            style={{
              border: `1px solid ${T.gray200}`,
              borderRadius: R.md,
              overflow: 'hidden',
              background: T.white,
            }}
          >
            <div
              style={{
                background: T.gray100,
                padding: '8px 12px',
                display: 'flex',
                gap: 4,
              }}
            >
              {['#FC605B', '#FDBC40', '#34C84A'].map((c) => (
                <span
                  key={c}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: c,
                  }}
                />
              ))}
            </div>
            <MailField label="From:" value={`${fromName} <${fromEmail}>`} />
            <MailField
              label="Subject:"
              value={
                subject.trim() || 'Your email subject line will appear here'
              }
            />
            <MailField
              label="Preview:"
              muted
              value={
                preview.trim() || 'Preview text shows below subject in inbox…'
              }
            />
            <div style={{ background: doc.settings.bg }}>
              <EmailMiniPreview
                doc={doc}
                ctx={ctx}
                segments={segments}
                preview={preview}
                subject={subject}
                device={device}
              />
            </div>
          </div>
        </>
      )}

      <div style={{ ...sectionLabel, marginTop: 20, marginBottom: 10 }}>
        Estimated Reach
      </div>
      <div
        style={{
          background: T.gray50,
          border: `1px solid ${T.gray200}`,
          borderRadius: R.md,
          padding: '12px 14px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            marginBottom: 4,
          }}
        >
          {reach.dots.map((d, i) => (
            <span
              key={`${d}-${i}`}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: d,
                flexShrink: 0,
              }}
            />
          ))}
          <span style={{ fontSize: 11.5, color: T.gray500, marginLeft: 3 }}>
            {reach.label}
          </span>
        </div>
        {reach.kind === 'flow' ? (
          <div style={{ fontSize: 12.5, color: T.gray600, marginTop: 4 }}>
            Auto-targeted — customers who trigger the automation
          </div>
        ) : (
          <>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: T.gray800,
                marginBottom: 6,
              }}
            >
              {reach.loading ? '…' : fmt(reach.total)}
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                fontSize: 11.5,
                color: T.gray400,
              }}
            >
              {/* Only the campaign's own channel: of the audience, how many
                  can actually receive it (opted in / reachable). */}
              <span>
                <strong style={{ color: T.gray700 }}>
                  {reach.loading
                    ? '…'
                    : fmt(isSms ? reach.smsEligible : reach.emailEligible)}
                </strong>{' '}
                can receive {isSms ? 'SMS' : 'email'}
              </span>
            </div>
          </>
        )}
      </div>
    </aside>
  )
}
